import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/Breadcrumbs";
import { Input } from "@/components/ui/input";
import { Search, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const ICON_MAP = {
  CreditCard: "💳", XCircle: "❌", Map: "🗺️", Ticket: "🎟️", Car: "🚗",
  HelpCircle: "❓", Phone: "📞", Globe: "🌐",
};

function FAQItem({ item, isOpen, toggle }) {
  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid={`faq-item-${item.id}`}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/40 transition-colors"
        data-testid={`faq-toggle-${item.id}`}
      >
        <span className="font-medium text-sm pr-4">{item.question}</span>
        {isOpen
          ? <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" />
          : <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
        }
      </button>
      {isOpen && (
        <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">
          {item.answer}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const { settings } = useSettings();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/faq/categories"),
      api.get("/faq"),
    ]).then(([catRes, itemRes]) => {
      setCategories(catRes.data);
      setItems(itemRes.data);
      setLoading(false);
    });
  }, []);

  const filtered = items.filter((it) => {
    if (activeCat && it.category_id !== activeCat) return false;
    if (search) {
      const q = search.toLowerCase();
      return it.question.toLowerCase().includes(q) || it.answer.toLowerCase().includes(q);
    }
    return true;
  });

  const breadcrumbItems = [{ label: "Home", to: "/" }, { label: "FAQ" }];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": filtered.slice(0, 20).map((it) => ({
      "@type": "Question",
      "name": it.question,
      "acceptedAnswer": { "@type": "Answer", "text": it.answer },
    })),
  };

  return (
    <div data-testid="faq-page">
      <SEO
        title="Frequently Asked Questions"
        description={`Find answers to common questions about booking tours, entry tickets, hotels, cabs and payments on ${settings.site_name || "HyderabadTourism.in"}`}
        schemas={[buildBreadcrumbSchema(breadcrumbItems), faqSchema]}
      />

      <section className="bg-gradient-to-b from-secondary/40 to-background pt-8 pb-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-2">Frequently Asked Questions</h1>
          <p className="text-muted-foreground max-w-2xl">
            Quick answers to everything about bookings, payments, cancellations and travel with {settings.site_name || "HyderabadTourism.in"}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search FAQs..."
            className="pl-10"
            data-testid="faq-search"
          />
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="faq-category-tabs">
          <button
            onClick={() => setActiveCat(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!activeCat ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80 text-foreground"}`}
            data-testid="faq-cat-all"
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCat(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCat === cat.id ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80 text-foreground"}`}
              data-testid={`faq-cat-${cat.id}`}
            >
              <span className="mr-1.5">{ICON_MAP[cat.icon] || "❓"}</span>
              {cat.name}
            </button>
          ))}
        </div>

        {/* FAQ items */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No FAQs found. Try a different search or category.</p>
        ) : (
          <div className="space-y-3" data-testid="faq-list">
            {filtered.map((it) => (
              <FAQItem
                key={it.id}
                item={it}
                isOpen={openId === it.id}
                toggle={() => setOpenId(openId === it.id ? null : it.id)}
              />
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 text-center p-8 rounded-2xl bg-muted/50 border border-border">
          <h2 className="font-display text-xl mb-2">Still have questions?</h2>
          <p className="text-muted-foreground text-sm mb-4">
            Our team is happy to help. Reach out on WhatsApp for instant support.
          </p>
          <a
            href={`https://wa.me/${settings.whatsapp_number || "919000000000"}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-green-700 transition-colors"
            data-testid="faq-whatsapp-cta"
          >
            <MessageCircle className="w-4 h-4" />
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
